import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

/**
 * BrandMarquee — Auto-scrolling horizontal band of real brand cases.
 *
 * Fetches published events (prioritising featured), and renders an infinite,
 * seamless left-to-right marquee. Pauses on hover. Links to /galeria.
 */
export default function BrandMarquee({ limit = 14, speedSeconds = 40, className = "" }) {
    const { tr } = useLocale();
    const [items, setItems] = useState([]);

    useEffect(() => {
        let alive = true;
        api.get(`/events?limit=100`)
            .then((r) => {
                if (!alive) return;
                const data = r.data || [];
                // Prioritise featured, then keep original order (which is `order` asc).
                const sorted = [...data].sort((a, b) => {
                    if (a.featured && !b.featured) return -1;
                    if (!a.featured && b.featured) return 1;
                    return 0;
                });
                // Keep only those with images, limit to `limit`.
                const withImg = sorted.filter((e) => !!e.image_url).slice(0, limit);
                setItems(withImg);
            })
            .catch(() => alive && setItems([]));
        return () => { alive = false; };
    }, [limit]);

    if (!items.length) return null;

    // Duplicate so the CSS marquee loops seamlessly.
    const doubled = [...items, ...items];

    return (
        <section
            data-testid="home-brand-marquee"
            className={`relative overflow-hidden border-y border-border bg-muted/40 ${className}`}
        >
            <div className="container-x pt-12 pb-6 sm:pt-16 sm:pb-8">
                <div className="flex items-end justify-between gap-6 flex-wrap">
                    <div className="max-w-2xl">
                        <span className="inline-flex items-center gap-3 text-[10px] sm:text-xs font-bold uppercase tracking-[0.28em] text-primary/70">
                            <span aria-hidden className="inline-block h-[1.5px] w-7 bg-secondary" />
                            {tr("Casos reais", "Real cases")}
                        </span>
                        <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-primary leading-tight">
                            {tr("Marcas que já ", "Brands already ")}
                            <span className="serif-accent text-secondary">
                                {tr("rodam", "rolling")}
                            </span>
                            {tr(" em NOVIFOOD.", " with NOVIFOOD.")}
                        </h2>
                    </div>
                    <Link
                        to="/galeria"
                        data-testid="home-marquee-cta"
                        className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline"
                    >
                        {tr("Ver galeria completa", "See full gallery")}
                        <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>

            {/* MARQUEE STRIP */}
            <div className="relative marquee-mask pb-14 sm:pb-16">
                <div
                    className="marquee-track flex gap-5 will-change-transform"
                    style={{ animationDuration: `${speedSeconds}s` }}
                    data-testid="home-marquee-track"
                >
                    {doubled.map((ev, i) => (
                        <MarqueeCard key={`${ev.id}-${i}`} ev={ev} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function MarqueeCard({ ev }) {
    return (
        <Link
            to="/galeria"
            className="shrink-0 group relative w-[240px] sm:w-[280px] md:w-[320px] aspect-[4/3] rounded-2xl overflow-hidden border border-border bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-secondary/40"
            aria-label={ev.brand}
        >
            <img
                src={ev.image_url}
                alt={ev.brand}
                loading="lazy"
                className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-90 pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
                <div className="font-heading text-base sm:text-lg font-extrabold text-white leading-tight drop-shadow-sm">
                    {ev.brand}
                </div>
                {ev.title && (
                    <div className="mt-0.5 text-[11px] sm:text-xs text-white/85 line-clamp-1">
                        {ev.title}
                    </div>
                )}
            </div>
        </Link>
    );
}
